import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Video, Volume2, VolumeX, MapPin, Users, Lock } from 'lucide-react';

// Same lazy MuxPlayer CoursePage uses, so the player chunk is shared.
const MuxPlayer = lazy(() => import('@mux/mux-player-react'));

const STATIC_ROOT = '/api/s3/';
const resolveImage = (src?: string | null) =>
  !src ? null : (src.startsWith('http') || src.startsWith('/api/') ? src : `${STATIC_ROOT}${src}`);

interface LiveItem {
  productId: string;
  title: string;
  instructor: string | null;
  coverImage: string | null;
  productType: string | null;
  startsAt: string;
  endsAt: string;
  registeredCount: number;
}
interface UpcomingItem extends LiveItem {
  price: number | null;
  memberPriceCents: number | null;
  locationMode: string;
  restrictedTo: string | null;
  registeredThisWeek: number;
  sessionCount?: number;    // all dates the product has
  remainingDates?: number;  // upcoming dates, including this one
  cohort?: boolean;         // one enrollment covers every date
}
interface Clip {
  kind: 'preview' | 'replay';
  productId: string;
  title?: string;
  productTitle?: string;
  chapterTitle?: string;
  instructor: string | null;
  playbackId: string;
  aspectRatio?: string;
  startSec: number;
  durationSec: number;
  cta: string;
  startsAt?: string | null;
  restrictedTo?: string | null;
  replayChapterCount?: number;
}
interface DiscoverPayload {
  live: LiveItem[];
  upcoming: UpcomingItem[];
  clips: Clip[];
}

const forClients = (label: string) => `For ${label} clients`;

const formatWhen = (iso: string) => {
  const d = new Date(iso);
  const day = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' }).format(d);
  const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(d);
  return `${day} · ${time}`;
};

const formatCountdown = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `Starts in ${h}h ${pad(m)}m ${pad(r)}s` : `Starts in ${m}m ${pad(r)}s`;
};

const formatPrice = (cents: number) => {
  const dollars = cents / 100;
  return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
};

// One shared ticking clock, only while something on screen needs it.
function useNow(active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

// "16:9" -> 1.777. Falls back to landscape, which is what most previews are.
const aspectOf = (ar?: string) => {
  const m = String(ar || '').match(/^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/);
  const v = m ? Number(m[1]) / Number(m[2]) : 16 / 9;
  return isFinite(v) && v > 0 ? v : 16 / 9;
};

// ── Live now / Up next strip ────────────────────────────────────────────────

const StripCard: React.FC<{ item: LiveItem | UpcomingItem; live: boolean; now: number }> = ({ item, live, now }) => {
  const up = item as UpcomingItem;
  const cover = resolveImage(item.coverImage);
  const msToStart = new Date(item.startsAt).getTime() - now;
  const nextTime = msToStart < 24 * 3600 * 1000 ? formatCountdown(msToStart) : formatWhen(item.startsAt);
  // A multi-date product shows once: a course as "8 classes · starts …", a series
  // you join date by date as "… · +3 more dates".
  const sessions = up.sessionCount || 1, more = (up.remainingDates || 1) - 1;
  // Once a course has started, count what's left, not what it had.
  const left = up.remainingDates || 1;
  const when = live
    ? `Live now · ${item.registeredCount} registered`
    : up.cohort && sessions > 1 && left < sessions
      ? `${left === 1 ? 'Just one class left' : `${left} classes left`} · ${nextTime}`
    : up.cohort && sessions > 1
      ? `${sessions} classes · ${msToStart < 24 * 3600 * 1000 ? nextTime : `starts ${nextTime}`}`
      : more > 0 ? `${nextTime} · +${more} more ${more === 1 ? 'date' : 'dates'}` : nextTime;
  return (
    <Link
      to={`/course/${item.productId}`}
      className="flex-shrink-0 flex flex-col overflow-hidden transition-transform hover:-translate-y-0.5"
      style={{
        width: 260,
        scrollSnapAlign: 'start',
        borderRadius: 'var(--hj-r-md)',
        background: 'var(--hj-surface-1)',
        border: live ? '1px solid oklch(0.62 0.22 25)' : '1px solid var(--hj-hairline)',
        color: 'var(--hj-ink)',
        textDecoration: 'none',
      }}
    >
      <div className="relative" style={{ aspectRatio: '16 / 9', background: 'var(--hj-beige)' }}>
        {cover ? (
          <img src={cover} alt="" loading="lazy" className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ color: 'var(--hj-beige-ink)' }}>
            <Video size={28} strokeWidth={1.6} />
          </div>
        )}
        {live && (
          <span
            className="absolute inline-flex items-center"
            style={{ top: 8, left: 8, gap: 6, padding: '3px 9px', borderRadius: 999, background: 'oklch(0 0 0 / 0.72)', color: '#fff', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.04em' }}
          >
            <span className="relative inline-flex" style={{ width: 8, height: 8 }}>
              <span className="absolute inline-flex h-full w-full rounded-full animate-ping" style={{ background: 'oklch(0.62 0.22 25)', opacity: 0.75 }} />
              <span className="relative inline-flex rounded-full" style={{ width: 8, height: 8, background: 'oklch(0.62 0.22 25)' }} />
            </span>
            LIVE
          </span>
        )}
      </div>
      <div className="flex flex-col" style={{ padding: '10px 12px 12px', gap: 4 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: live ? 'oklch(0.62 0.22 25)' : 'var(--hj-ink)', fontVariantNumeric: 'tabular-nums' }}>
          {when}
        </div>
        <div className="line-clamp-2" style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.25 }}>{item.title}</div>
        {item.instructor && <div className="line-clamp-1" style={{ fontSize: 13, color: 'var(--hj-ink-2)' }}>{item.instructor}</div>}
        {!live && (
          <div className="flex flex-col" style={{ gap: 3, marginTop: 4, fontSize: 12.5, color: 'var(--hj-ink-2)' }}>
            {up.memberPriceCents === 0 && (
              <span style={{ color: 'var(--hj-ink)', fontWeight: 600 }}>Free for Hey Joe Plus members</span>
            )}
            {up.memberPriceCents !== 0 && typeof up.price === 'number' && (
              <span style={{ color: 'var(--hj-ink)' }}>{up.price === 0 ? 'Free' : formatPrice(up.price)}</span>
            )}
            {up.locationMode && up.locationMode !== 'online' && (
              <span className="inline-flex items-center" style={{ gap: 4 }}><MapPin size={12} /> {up.locationMode === 'both' ? 'In person or online' : 'In person'}</span>
            )}
            {up.registeredThisWeek > 0 && (
              <span className="inline-flex items-center" style={{ gap: 4 }}><Users size={12} /> {up.registeredThisWeek} registered this week</span>
            )}
            {up.restrictedTo && (
              <span className="inline-flex items-center" style={{ gap: 4, color: 'var(--hj-ink)' }}><Lock size={12} /> {forClients(up.restrictedTo)}</span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

// ── Clip feed ──────────────────────────────────────────────────────────────

interface ClipCardProps {
  clip: Clip;
  index: number;
  active: boolean;
  mount: boolean;          // only the active and next clip get a player
  muted: boolean;
  onToggleMute: () => void;
  now: number;
}

const ClipCard = React.forwardRef<HTMLElement, ClipCardProps>(({ clip, index, active, mount, muted, onToggleMute, now }, ref) => {
  const playerRef = useRef<any>(null);
  const start = clip.startSec || 0;
  const end = start + clip.durationSec;
  const ratio = aspectOf(clip.aspectRatio);
  const poster = `https://image.mux.com/${clip.playbackId}/thumbnail.jpg?time=${Math.floor(start)}&width=720`;
  const heading = clip.kind === 'replay' ? (clip.productTitle || clip.title) : clip.title;

  useEffect(() => {
    const el = playerRef.current;
    if (!el) return;
    if (active) {
      const p = el.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      el.pause?.();
      try { el.currentTime = start; } catch { /* not ready yet */ }
    }
  }, [active, mount, start]);

  useEffect(() => {
    if (playerRef.current) playerRef.current.muted = muted;
  }, [muted]);

  // Loop the teaser segment, not the whole asset.
  const onTime = useCallback(() => {
    const el = playerRef.current;
    if (el && el.currentTime >= end) el.currentTime = start;
  }, [start, end]);
  const onEnded = useCallback(() => {
    const el = playerRef.current;
    if (!el) return;
    el.currentTime = start;
    if (active) el.play?.()?.catch?.(() => {});
  }, [start, active]);

  const msToStart = clip.startsAt ? new Date(clip.startsAt).getTime() - now : null;
  const whenLine = msToStart !== null && msToStart > 0
    ? (msToStart < 24 * 3600 * 1000 ? formatCountdown(msToStart) : formatWhen(clip.startsAt!))
    : null;

  return (
    <section
      ref={ref}
      data-index={index}
      className="relative flex items-center justify-center"
      style={{ height: '100%', scrollSnapAlign: 'start', scrollSnapStop: 'always', background: '#0A0A0A' }}
    >
      {/* The frame keeps the clip's own shape: a 16:9 preview letterboxes on black
          rather than being cropped to fill a phone screen. */}
      <div
        className="relative"
        style={{ width: `min(100%, calc((100dvh - 68px) * ${ratio}))`, aspectRatio: String(ratio), maxHeight: '100%', maxWidth: ratio < 1 ? 520 : 1000 }}
        onClick={onToggleMute}
      >
        <img src={poster} alt="" className="absolute inset-0 w-full h-full object-contain" loading={index < 2 ? 'eager' : 'lazy'} />
        {mount && (
          <Suspense fallback={null}>
            <MuxPlayer
              ref={playerRef}
              playbackId={clip.playbackId}
              streamType="on-demand"
              startTime={start}
              muted={muted}
              playsInline
              preload="auto"
              nohotkeys
              poster={poster}
              onTimeUpdate={onTime}
              onEnded={onEnded}
              metadata={{ video_title: `${heading} (Discover)` }}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', ['--controls' as any]: 'none', ['--media-object-fit' as any]: 'contain', background: 'transparent' }}
            />
          </Suspense>
        )}
      </div>

      {/* Overlay: text over a bottom gradient so it reads on any frame. */}
      <div
        className="absolute inset-x-0 bottom-0 pointer-events-none"
        style={{ padding: '72px 20px 28px', background: 'linear-gradient(to top, oklch(0 0 0 / 0.85), oklch(0 0 0 / 0))' }}
      >
        <div className="mx-auto flex flex-col pointer-events-auto" style={{ maxWidth: 640, gap: 6, color: '#fff' }}>
          {clip.chapterTitle && (
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--hj-yellow)' }}>{clip.chapterTitle}</div>
          )}
          {whenLine && (
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--hj-yellow)', fontVariantNumeric: 'tabular-nums' }}>{whenLine}</div>
          )}
          <div className="font-hj-display" style={{ fontSize: 'clamp(22px, 4.5vw, 30px)', lineHeight: 1.1, letterSpacing: '-0.01em' }}>{heading}</div>
          {clip.instructor && <div style={{ fontSize: 15, color: 'oklch(0.92 0 0)' }}>{clip.instructor}</div>}
          {(clip.restrictedTo || clip.replayChapterCount) && (
            <div className="flex flex-wrap" style={{ gap: 10, fontSize: 13, color: 'oklch(0.9 0 0)' }}>
              {clip.restrictedTo && <span className="inline-flex items-center" style={{ gap: 4 }}><Lock size={12} /> {forClients(clip.restrictedTo)}</span>}
              {clip.replayChapterCount ? <span>{clip.replayChapterCount} chapters in the replay</span> : null}
            </div>
          )}
          <div className="flex items-center" style={{ gap: 10, marginTop: 10 }}>
            <Link
              to={`/course/${clip.productId}`}
              className="inline-flex items-center justify-center"
              style={{ flex: 1, maxWidth: 360, minHeight: 50, padding: '0 22px', borderRadius: 999, background: 'var(--hj-yellow)', color: '#0A0A0A', fontSize: 16, fontWeight: 700, textDecoration: 'none' }}
            >
              {clip.cta}
            </Link>
            <button
              type="button"
              onClick={onToggleMute}
              aria-label={muted ? 'Turn sound on' : 'Turn sound off'}
              className="inline-flex items-center justify-center"
              style={{ width: 50, height: 50, borderRadius: 999, border: '1px solid oklch(1 0 0 / 0.35)', background: 'oklch(0 0 0 / 0.45)', color: '#fff', cursor: 'pointer' }}
            >
              {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
});
ClipCard.displayName = 'ClipCard';

// ── Page ───────────────────────────────────────────────────────────────────

const DiscoverPage: React.FC = () => {
  const [data, setData] = useState<DiscoverPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeClip, setActiveClip] = useState(-1);
  const [muted, setMuted] = useState(true);
  const clipRefs = useRef<(HTMLElement | null)[]>([]);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(() => {
    setError(null);
    fetch('/api/discover')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: DiscoverPayload) => setData({ live: d.live || [], upcoming: d.upcoming || [], clips: d.clips || [] }))
      .catch(() => setError('Discover could not load right now.'));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Discover | Hey Joe Plus';
    return () => { document.title = prev; };
  }, []);

  // Whichever clip is mostly on screen is the one that plays.
  useEffect(() => {
    if (!data?.clips.length) return;
    const obs = new IntersectionObserver(entries => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          setActiveClip(Number((e.target as HTMLElement).dataset.index));
        }
      }
    }, { root: scrollerRef.current, threshold: [0.6] });
    clipRefs.current.forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, [data]);

  // Tick only when a countdown is actually on screen (anything within 24h).
  const needsTick = !!data && [...data.upcoming, ...data.clips.filter(c => c.startsAt) as any[]]
    .some((x: any) => x.startsAt && new Date(x.startsAt).getTime() - Date.now() < 24 * 3600 * 1000);
  const now = useNow(needsTick);

  const strip = data ? [...data.live.map(i => ({ i, live: true })), ...data.upcoming.map(i => ({ i, live: false }))] : [];

  return (
    <div
      ref={scrollerRef}
      style={{
        height: 'calc(100dvh - 68px)',
        overflowY: 'auto',
        scrollSnapType: 'y mandatory',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
      }}
    >
      <div style={{ scrollSnapAlign: 'start' }}>
        <div className="mx-auto" style={{ maxWidth: 1120, padding: '0 16px 24px' }}>
          <section style={{ margin: '20px 0 18px', padding: '20px 22px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
            <h1 className="font-hj-display m-0" style={{ fontSize: 'clamp(28px, 3vw, 38px)', lineHeight: 1.05, letterSpacing: '-0.02em', color: 'var(--hj-beige-ink)' }}>
              Discover
            </h1>
            <p className="m-0" style={{ marginTop: 6, fontSize: 14.5, lineHeight: 1.45, maxWidth: 560, color: 'var(--hj-beige-ink)' }}>
              What's live on Hey Joe Plus right now, what's coming up, and short previews from the people teaching it.
            </p>
          </section>

          {!data && !error && (
            <div className="flex items-center justify-center" style={{ padding: '48px 0', color: 'var(--hj-ink-2)' }}>
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}
          {error && (
            <div className="flex flex-col items-center" style={{ padding: '40px 0', gap: 12, color: 'var(--hj-ink)' }}>
              <p className="m-0">{error}</p>
              <button type="button" onClick={load} style={{ padding: '10px 18px', borderRadius: 999, border: '1px solid var(--hj-hairline)', background: 'var(--hj-surface-1)', color: 'var(--hj-ink)', cursor: 'pointer', fontFamily: 'inherit' }}>
                Try again
              </button>
            </div>
          )}

          {data && (
            <>
              <h2 className="mono-label m-0" style={{ marginBottom: 10, color: 'var(--hj-ink-2)' }}>
                {data.live.length ? 'Live now · Up next' : 'Up next'}
              </h2>
              {strip.length ? (
                <div
                  className="flex overflow-x-auto"
                  style={{ gap: 12, paddingBottom: 8, scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
                >
                  {strip.map(({ i, live }) => (
                    <StripCard key={`${i.productId}-${i.startsAt}`} item={i} live={live} now={now} />
                  ))}
                </div>
              ) : (
                <p className="m-0" style={{ padding: '14px 0', color: 'var(--hj-ink)' }}>
                  Nothing is scheduled right now. <Link to="/marketplace" style={{ color: 'var(--hj-ink)', textDecoration: 'underline' }}>Browse the marketplace</Link> for courses and replays.
                </p>
              )}
              {data.clips.length > 0 && (
                <p className="m-0" style={{ marginTop: 18, fontSize: 13.5, color: 'var(--hj-ink-2)' }}>
                  Scroll for previews. Tap a video to turn the sound on.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {data && data.clips.length === 0 && (
        <div className="mx-auto flex flex-col items-center text-center" style={{ maxWidth: 520, padding: '24px 16px 64px', gap: 8, color: 'var(--hj-ink)' }}>
          <Video size={28} strokeWidth={1.6} />
          <p className="m-0">No previews to show yet. Check back soon.</p>
        </div>
      )}

      {data?.clips.map((clip, idx) => (
        <ClipCard
          key={`${clip.productId}-${clip.playbackId}-${clip.startSec}`}
          ref={el => { clipRefs.current[idx] = el; }}
          clip={clip}
          index={idx}
          active={idx === activeClip}
          mount={activeClip >= 0 ? (idx === activeClip || idx === activeClip + 1) : idx === 0}
          muted={muted}
          onToggleMute={() => setMuted(m => !m)}
          now={now}
        />
      ))}
    </div>
  );
};

export default DiscoverPage;
